// ─────────────────────────────────────────────────────────────────────────────
// margin_utility.ts — Functional gutter marks
//
// Utility margin decorations anchor snug to the body content box (in the
// inner gutter). Used for ruler-like / functional marks: simple rule,
// double rule, hash marks, stipple, line-head diamonds.
//
// Themed ornaments that occupy the margin band more freely live in
// margin_graphics.ts. Frames around the body content rect live in
// frame_decorations.ts. Renderer order: graphics → utility → frame.
// ─────────────────────────────────────────────────────────────────────────────

import type { PageLayout } from './page_renderer';

export type MarginPlacement = 'gutter' | 'line-head';

export interface MarginDrawOpts {
  /** Active body-justification style id — used by 'line-head' decorations
   *  to align with the underlying ruled-line spacing. */
  justification?: string;
}

export interface MarginUtility {
  id: string;
  label: string;
  placement: MarginPlacement;
  draw(
    ctx: CanvasRenderingContext2D,
    layout: PageLayout,
    opts: MarginDrawOpts,
  ): void;
}

/** Vertical band utility marks may occupy — skips the header strip + a
 *  little bottom inset so marks never clash with page chrome. */
export const HEADER_RESERVED = 44;
export const FOOTER_INSET    = 16;

export function marginDrawBox(L: PageLayout): { top: number; bottom: number } {
  return {
    top:    L.y + HEADER_RESERVED,
    bottom: L.y + L.height - FOOTER_INSET,
  };
}

function gutter(L: PageLayout): {
  leftX: number; rightX: number;
  top: number; bottom: number;
} {
  const box = marginDrawBox(L);
  return {
    leftX:  L.x + L.marginLeft - 12,
    rightX: L.x + L.width - L.marginRight + 12,
    top:    Math.max(box.top, L.y + L.marginTop),
    bottom: Math.min(box.bottom, L.y + L.height - L.marginBottom),
  };
}

function justificationLineSpacing(justification: string | undefined): number {
  switch (justification) {
    case 'lines':   return 28;
    case 'dots':    return 24;
    case 'grid':    return 20;
    case 'college': return 22;
    case 'wide':    return 36;
    case 'cornell': return 26;
    default:        return 28;
  }
}

// ── Built-ins ───────────────────────────────────────────────────────────────

const NONE: MarginUtility = {
  id: 'none',
  label: 'None',
  placement: 'gutter',
  draw: () => { /* nothing */ },
};

const SIMPLE_RULE: MarginUtility = {
  id: 'simple-rule',
  label: 'Simple rule',
  placement: 'gutter',
  draw(ctx, L) {
    const g = gutter(L);
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.4)';
    ctx.lineWidth = 0.75;
    ctx.beginPath();
    ctx.moveTo(g.leftX, g.top);  ctx.lineTo(g.leftX, g.bottom);
    ctx.moveTo(g.rightX, g.top); ctx.lineTo(g.rightX, g.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

const DOUBLE_RULE: MarginUtility = {
  id: 'double-rule',
  label: 'Double rule',
  placement: 'gutter',
  draw(ctx, L) {
    const g = gutter(L);
    const offset = 4;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.42)';
    ctx.lineWidth = 0.85;
    ctx.beginPath();
    ctx.moveTo(g.leftX,  g.top); ctx.lineTo(g.leftX,  g.bottom);
    ctx.moveTo(g.rightX, g.top); ctx.lineTo(g.rightX, g.bottom);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.22)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(g.leftX  - offset, g.top); ctx.lineTo(g.leftX  - offset, g.bottom);
    ctx.moveTo(g.rightX + offset, g.top); ctx.lineTo(g.rightX + offset, g.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

const DIAMONDS: MarginUtility = {
  id: 'diamonds',
  label: 'Diamond chain',
  placement: 'line-head',
  draw(ctx, L, opts) {
    const g = gutter(L);
    const spacing = justificationLineSpacing(opts.justification);
    const size = 3;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.5)';
    ctx.fillStyle = 'rgba(110, 80, 50, 0.22)';
    ctx.lineWidth = 0.6;
    for (let ly = g.top + spacing; ly <= g.bottom; ly += spacing) {
      ctx.beginPath();
      ctx.moveTo(g.leftX, ly - size);
      ctx.lineTo(g.leftX + size, ly);
      ctx.lineTo(g.leftX, ly + size);
      ctx.lineTo(g.leftX - size, ly);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(110, 80, 50, 0.12)';
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.25)';
    for (let ly = g.top + spacing; ly <= g.bottom; ly += spacing) {
      ctx.beginPath();
      ctx.moveTo(g.rightX, ly - size);
      ctx.lineTo(g.rightX + size, ly);
      ctx.lineTo(g.rightX, ly + size);
      ctx.lineTo(g.rightX - size, ly);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  },
};

const HASH_MARKS: MarginUtility = {
  id: 'hash-marks',
  label: 'Hash marks',
  placement: 'gutter',
  draw(ctx, L) {
    const g = gutter(L);
    const spacing = 14;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.5)';
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    for (let ly = g.top; ly <= g.bottom; ly += spacing) {
      const major = Math.round((ly - g.top) / spacing) % 5 === 0;
      const len = major ? 8 : 4;
      ctx.moveTo(g.leftX, ly);  ctx.lineTo(g.leftX + len, ly);
      ctx.moveTo(g.rightX, ly); ctx.lineTo(g.rightX - len, ly);
    }
    ctx.stroke();
    ctx.restore();
  },
};

const STIPPLE_BAND: MarginUtility = {
  id: 'stipple-band',
  label: 'Stipple band',
  placement: 'gutter',
  draw(ctx, L) {
    const g = gutter(L);
    const stride = 4;
    const cols = 3;
    ctx.save();
    ctx.fillStyle = 'rgba(110, 80, 50, 0.45)';
    for (const baseX of [g.leftX - (cols - 1) * 2, g.rightX]) {
      for (let c = 0; c < cols; c++) {
        const cx = baseX + c * 4;
        for (let ly = g.top; ly <= g.bottom; ly += stride) {
          const off = c % 2 === 0 ? 0 : stride / 2;
          ctx.beginPath();
          ctx.arc(cx, ly + off, 0.55, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
  },
};

// ── Registry ────────────────────────────────────────────────────────────────

const REGISTRY: Map<string, MarginUtility> = new Map();
function register(d: MarginUtility): void { REGISTRY.set(d.id, d); }

register(NONE);
register(SIMPLE_RULE);
register(DOUBLE_RULE);
register(DIAMONDS);
register(HASH_MARKS);
register(STIPPLE_BAND);

export function getMarginUtility(id: string | undefined | null): MarginUtility {
  if (!id) return NONE;
  return REGISTRY.get(id) ?? NONE;
}

export function listMarginUtilities(): MarginUtility[] {
  return [...REGISTRY.values()];
}
